/**
 * miniPolarMap — the memory-update card's "Charted Atlas" mini polar map.
 *
 * A miniature of the Progress tab's atlas: every topic's full slice always
 * exists as a visible pale mist sector (territory still to chart); charted
 * understanding is an annular wedge in the topic's full identity hue
 * (topicPalette) — depth is encoded by radius alone.
 * The card's own topic is the "front" being written: its wedge is
 * always present (even at zero) so the write animation always has a body,
 * and as it grows it pushes its mist back geometrically.
 *
 *     buildMiniMap(svg, { topics, activeTopicId, activeFrom })
 *       → { setActive, ripple, playUpdate, destroy }
 *
 * `playUpdate({ from, to, onLanded })` owns the whole update beat — survey
 * scan → gather → write (constant-size pop, honest landing + ripple) → edge
 * sharpen + outward settle wave — on ONE absolute-time rAF clock, so a
 * backgrounded tab resumes at the right phase instead of stretching the
 * sequence past `CARD_HOLD_MS`. Reduced motion jumps to the final state and
 * still calls `onLanded`. Purely cosmetic two-step accents (mist bumps, the
 * edge flash) use tracked timers that `destroy()` clears.
 *
 * **UNITS: everything this module accepts is a 0–1 fraction.** The 0–100
 * integers the backend sends are normalized once, in
 * `understandingCard.parseTopicUnderstanding`. (`topicFill` takes no depth —
 * a topic's hue is constant — so no unit conversion crosses that seam.)
 *
 * There is deliberately NO sliver floor and NO delta-proportional overshoot
 * (the old pair silently cancelled each other for small first writes):
 * `radiusOf` hard-clamps to [0,1] — the back-ease anticipation dip pins at
 * the hole rather than inverting the sector — and the pop is a constant
 * `POP` radius units past the honest target, clamped below R and gated on
 * `to > 0`, always settling back exactly.
 *
 * @module components/brain/miniPolarMap
 */

import { polar, wedgePath, depthRadiusFrac } from '../PolarProgressChart.js';
import { topicFill, MIST_FILL, MIST_FILL_HOT } from '../topicPalette.js';

const NS = 'http://www.w3.org/2000/svg';

/** Survey scan beat (ms): a hairline lap from the active slot. */
export const SCAN_MS = 500;
/** Gather beat (ms): the active slot's mist thickens before it parts. */
export const GATHER_MS = 260;
/** Write sweep length (ms). Locked with the ease-in-out-back curve below. */
export const MAP_ANIMATION_MS = 1250;
/** Edge sharpen + outward settle wave (ms), closing the beat. */
export const SETTLE_MS = 600;
/**
 * Total time a live card keeps moving after its own fade-in — the amount
 * every later chat block must hold before it appears. Consumed by
 * `understandingCardHoldMs`.
 */
export const CARD_HOLD_MS = SCAN_MS + GATHER_MS + MAP_ANIMATION_MS + SETTLE_MS;

/**
 * Dramatized minimum delta, in 0–1 units. Real per-section deltas are small
 * (~0.03 plain, ~0.08 framework-weighted) and the L1 flash card's delta is
 * structurally zero. The card sweeps from `min(from, progress - DRAMA_DELTA)`
 * up to the EXACT true value: the motion is guaranteed substantial, the
 * destination always honest. The milestone pill never reads it (it keys off
 * the real `from`).
 */
export const DRAMA_DELTA = 0.12;

/**
 * Constant-size overshoot past the honest target, in viewBox radius units —
 * the same visual pop for a 3-point first write and a 30-point deep write
 * (a delta-proportional pop is stage-variance smuggled into the easing).
 * Clamped below R at apply time and skipped entirely when `to` is 0.
 */
export const POP = 6;

// ── Canvas ────────────────────────────────────────────────────────────────
// A 112 viewBox rendered at 96–112px, 50 outer radius over a 13 hole,
// 3.4deg wedge gaps — proportionally wider than the full chart's because at
// this size hairline separations disappear.
const VIEWBOX = '0 0 112 112';
const CX = 56;
const CY = 56;
const R = 50;
const HOLE = 13;
// Identity-rim band width — the Progress chart's rim (7 of its 146-unit
// band, ~4.8%) scaled to this canvas's 37-unit band.
const RIM_W = 1.8;
const WEDGE_GAP_DEG = 3.4;

/** The map always draws twelve slots, however many topics the payload carried. */
const SLOT_COUNT = 12;

// The gather tone the active slot's mist deepens to before it parts.
const MIST_GATHER = '#d8e0e8';

// Ripple: one hairline circle starting at the wedge's final radius and
// expanding 20% of the outer radius, radius and fade sharing a cubic ease-out.
const RIPPLE_MS = 520;
const RIPPLE_EXPAND = R * 0.2;
const RIPPLE_OPACITY = 0.4;

// Settle wave: per-slot stagger, clockwise outward from the active slot.
const WAVE_STEP_MS = 45;

/**
 * Whether the user asked for reduced motion. Exported so the card module
 * reads the same answer through the same call.
 * @returns {boolean}
 */
export function prefersReducedMotion() {
  try {
    return matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * Ease-in-out back with `c1 = 2.6` — one S-curve with a slight anticipation
 * dip below 0 and a soft pop past 1 before gliding onto the target.
 * `f(0) === 0` and `f(1) === 1` exactly, which is what lets the sweep land
 * on the true value.
 * @param {number} t - Normalized time 0–1
 * @returns {number} Progress multiplier (may dip below 0 / exceed 1)
 */
export function easeInOutBack(t) {
  const c1 = 2.6;
  const c2 = c1 * 1.525;
  return t < 0.5
    ? (Math.pow(2 * t, 2) * ((c2 + 1) * 2 * t - c2)) / 2
    : (Math.pow(2 * t - 2, 2) * ((c2 + 1) * (2 * t - 2) + c2) + 2) / 2;
}

/**
 * Outer radius for a 0–1 depth value, through the shared low-end bias curve
 * (`depthRadiusFrac` — the same mapping the Progress chart draws with, so a
 * write lands at exactly the radius the chart later shows). THE single clamp
 * point: values below 0 (the back-ease anticipation dip) pin at the hole —
 * a degenerate-but-valid zero-width sector, never an inverted one.
 * @param {number} v - Depth, 0–1 (out-of-range input clamped)
 * @returns {number} Outer radius in viewBox units
 */
export function radiusOf(v) {
  const frac = Math.max(0, Math.min(1, Number.isFinite(v) ? v : 0));
  return HOLE + (R - HOLE) * depthRadiusFrac(frac);
}

/**
 * Create and append a namespaced SVG element with attributes.
 * @param {string} tag
 * @param {Object} [attrs]
 * @param {SVGElement} [parent]
 * @returns {SVGElement}
 * @private
 */
function svgEl(tag, attrs = {}, parent) {
  const el = document.createElementNS(NS, tag);
  for (const k in attrs) {
    if (Object.hasOwn(attrs, k)) el.setAttribute(k, String(attrs[k]));
  }
  if (parent) parent.appendChild(el);
  return el;
}

/**
 * Normalize the payload's topics into exactly `SLOT_COUNT` drawable slots and
 * locate the active one. Must render correctly with an empty or missing
 * `topics` array (an old envelope, malformed JSON): the active wedge plus
 * eleven pure-mist slots. When the active topic isn't found among the topics
 * — the OrientationMode demo card, which carries no topic id — the first
 * slot is borrowed for it.
 * @param {Array<{id?: string, progress?: number}>} topics - 0–1 progress values
 * @param {string|null} activeTopicId
 * @returns {{slots: Array<{id: string|null, progress: number}>, activeIndex: number}}
 * @private
 */
function toSlots(topics, activeTopicId) {
  const slots = (Array.isArray(topics) ? topics : []).slice(0, SLOT_COUNT).map((t) => ({
    id: t?.id ?? null,
    progress: Number.isFinite(Number(t?.progress)) ? Number(t.progress) : 0
  }));

  let activeIndex = activeTopicId ? slots.findIndex((s) => s.id === activeTopicId) : -1;
  if (activeIndex === -1) {
    if (slots.length === 0) slots.push({ id: activeTopicId, progress: 0 });
    activeIndex = 0;
    // A partial payload that lacks the active topic borrows slot 0 — the
    // borrowed slot must WEAR the active identity, or the wedge being written
    // renders in another topic's hue. The demo card (null activeTopicId)
    // keeps the borrowed slot's own identity so its wedge stays colored.
    if (activeTopicId) slots[0] = { ...slots[0], id: activeTopicId };
  }
  while (slots.length < SLOT_COUNT) slots.push({ id: null, progress: 0 });

  return { slots, activeIndex };
}

/**
 * Render the mini map into an `<svg>` and return its animation handle.
 *
 * @param {SVGSVGElement} svg - Empty (or reused) SVG host; its viewBox is set here
 * @param {Object} opts
 * @param {Array<{id?: string, label?: string, progress?: number}>} [opts.topics] -
 *   All twelve topics in display order, `progress` in 0–1 units
 * @param {string|null} [opts.activeTopicId] - Id of the card's own topic
 * @param {number} [opts.activeFrom=0] - Resting depth of the active wedge,
 *   0–1. The card passes its DRAMATIZED origin here, so nothing ever flashes
 *   the final value before the sweep starts.
 * @returns {{
 *   setActive: (v: number) => void,
 *   ripple: (finalV: number) => void,
 *   playUpdate: (opts: {from: number, to: number, onLanded?: () => void}) => Promise<void>,
 *   destroy: () => void
 * }} Handle. All values 0–1.
 */
export function buildMiniMap(svg, { topics = [], activeTopicId = null, activeFrom = 0 } = {}) {
  while (svg.firstChild) svg.removeChild(svg.firstChild);
  svg.setAttribute('viewBox', VIEWBOX);
  svg.setAttribute('aria-hidden', 'true');

  const { slots, activeIndex } = toSlots(topics, activeTopicId);
  const slice = 360 / SLOT_COUNT;
  const activeId = slots[activeIndex].id;

  let activeCore = null;
  let activeRim = null;
  let activeA0 = 0;
  let activeA1 = 0;
  const mists = [];

  // Mist inner edge: the charted front, clamped so the annulus never inverts
  // and always keeps a hairline of rim.
  const mistInner = (r) => Math.min(R - 0.4, Math.max(HOLE, r));

  slots.forEach((slot, i) => {
    const a0 = i * slice + WEDGE_GAP_DEG / 2;
    const a1 = (i + 1) * slice - WEDGE_GAP_DEG / 2;
    const isActive = i === activeIndex;
    const v = isActive ? activeFrom : slot.progress;

    // Charted core. The ACTIVE slot's core is always created — even at zero —
    // so the write animation always has a body to move (a zero-progress
    // non-active slot is pure mist instead).
    if (isActive) {
      activeCore = svgEl('path', {
        d: wedgePath(CX, CY, HOLE, Math.max(HOLE + 0.01, radiusOf(v)), a0, a1),
        fill: topicFill(activeId),
        class: 'tu-map__wedge tu-map__wedge--active'
      }, svg);
      activeA0 = a0;
      activeA1 = a1;
    } else if (slot.progress >= 0.01) {
      svgEl('path', {
        d: wedgePath(CX, CY, HOLE, radiusOf(slot.progress), a0, a1),
        fill: topicFill(slot.id),
        class: 'tu-map__wedge'
      }, svg);
    }

    // Mist over the uncharted remainder. Fully-charted slots keep the node
    // (so the scan/settle beats can address every slot) at zero opacity.
    const mist = svgEl('path', {
      d: wedgePath(CX, CY, mistInner(radiusOf(v)), R, a0, a1),
      fill: MIST_FILL,
      class: 'tu-map__mist'
    }, svg);

    // Identity rim — the same inner-edge seed the Progress chart draws,
    // occupying only the band the slot's core does NOT cover (rim and core
    // never stack; a core past the rim band has no rim). The ACTIVE slot's
    // rim is live-updated by setActive/setActiveRadius as its core writes
    // through it, so it is always created, even zero-width.
    const coreOuter = isActive
      ? Math.max(HOLE + 0.01, radiusOf(v))
      : slot.progress >= 0.01 ? radiusOf(slot.progress) : HOLE;
    const rimInner = Math.min(HOLE + RIM_W, Math.max(HOLE, coreOuter));
    if (isActive || rimInner < HOLE + RIM_W - 0.05) {
      const rim = svgEl('path', {
        d: wedgePath(CX, CY, rimInner, HOLE + RIM_W, a0, a1),
        fill: topicFill(isActive ? activeId : slot.id),
        class: 'tu-map__rim'
      }, svg);
      if (isActive) activeRim = rim;
    }
    if (v >= 0.99) mist.style.opacity = '0';
    mists.push(mist);
  });

  // Effects layer, above the wedges so hairlines are never clipped by one.
  const fxLayer = svgEl('g', { class: 'tu-map__fx' }, svg);

  let masterRaf = 0;
  let rippleRaf = 0;
  const timers = new Set();

  const later = (fn, ms) => {
    const id = setTimeout(() => { timers.delete(id); fn(); }, ms);
    timers.add(id);
  };

  /** Cosmetic mist accent: brief tone bump, then back. */
  const mistBump = (i, tone = MIST_FILL_HOT) => {
    const m = mists[i];
    if (!m || m.style.opacity === '0') return;
    m.style.transition = 'fill 120ms ease';
    m.style.fill = tone;
    later(() => {
      m.style.transition = 'fill 340ms ease';
      m.style.fill = MIST_FILL;
    }, 150);
  };

  /** Keep the active slot's rim to the band its core doesn't cover. */
  const setActiveRim = (coreOuter) => {
    if (!activeRim) return;
    const rimInner = Math.min(HOLE + RIM_W, Math.max(HOLE, coreOuter));
    activeRim.setAttribute('d', wedgePath(CX, CY, rimInner, HOLE + RIM_W, activeA0, activeA1));
  };

  const setActive = (v) => {
    if (!activeCore) return;
    const r = radiusOf(v);
    activeCore.setAttribute('d', wedgePath(CX, CY, HOLE, Math.max(HOLE + 0.01, r), activeA0, activeA1));
    setActiveRim(Math.max(HOLE + 0.01, r));
    const mist = mists[activeIndex];
    if (mist) {
      mist.setAttribute('d', wedgePath(CX, CY, mistInner(r), R, activeA0, activeA1));
      // A write landing at full depth retires its mist entirely, matching how
      // a fully-charted slot builds — no hairline rim left behind.
      mist.style.opacity = v >= 0.99 ? '0' : '';
    }
  };

  /** Radius-space variant used by the pop (which exceeds radiusOf's range). */
  const setActiveRadius = (r) => {
    if (!activeCore) return;
    const clamped = Math.max(HOLE, Math.min(R - 0.5, r));
    activeCore.setAttribute('d', wedgePath(CX, CY, HOLE, Math.max(HOLE + 0.01, clamped), activeA0, activeA1));
    setActiveRim(Math.max(HOLE + 0.01, clamped));
    const mist = mists[activeIndex];
    if (mist) mist.setAttribute('d', wedgePath(CX, CY, mistInner(clamped), R, activeA0, activeA1));
  };

  const ripple = (finalV) => {
    if (prefersReducedMotion()) return;
    const r0 = radiusOf(finalV);
    const color = topicFill(activeId);
    const circle = svgEl('circle', {
      cx: CX, cy: CY, r: r0, fill: 'none', stroke: color,
      'stroke-width': 1, 'stroke-opacity': RIPPLE_OPACITY, class: 'tu-map__ripple'
    }, fxLayer);

    let startTs = null;
    const frame = (ts) => {
      if (startTs === null) startTs = ts;
      const t = Math.min(1, (ts - startTs) / RIPPLE_MS);
      const eased = 1 - Math.pow(1 - t, 3);
      circle.setAttribute('r', String(r0 + RIPPLE_EXPAND * eased));
      circle.setAttribute('stroke-opacity', (RIPPLE_OPACITY * (1 - eased)).toFixed(3));
      if (t < 1) {
        rippleRaf = requestAnimationFrame(frame);
        return;
      }
      rippleRaf = 0;
      circle.remove();
    };
    rippleRaf = requestAnimationFrame(frame);
  };

  /**
   * The whole update beat on one absolute clock. Phases (from the start
   * timestamp): scan [0, SCAN_MS) → gather → write → sharpen + settle wave.
   * Resolves when the beat completes (or immediately under reduced motion /
   * after destroy()).
   * @param {{from: number, to: number, onLanded?: () => void}} opts
   * @returns {Promise<void>}
   */
  const playUpdate = ({ from, to, onLanded }) => new Promise((resolve) => {
    if (prefersReducedMotion()) {
      setActive(to);
      if (onLanded) onLanded();
      resolve();
      return;
    }

    const T_GATHER = SCAN_MS;
    const T_WRITE = SCAN_MS + GATHER_MS;
    const T_SETTLE = T_WRITE + MAP_ANIMATION_MS;
    const T_END = T_SETTLE + SETTLE_MS;

    const startA = activeIndex * slice + slice / 2;
    const scanLine = svgEl('line', {
      stroke: '#aebfcc', 'stroke-width': 1, 'stroke-linecap': 'round',
      'stroke-opacity': 0.55, class: 'tu-map__scanline'
    }, fxLayer);

    const rFrom = radiusOf(from);
    const rTo = radiusOf(to);
    let lastScanDeg = 0;
    let gathered = false;
    let parted = false;
    let landed = false;
    let sharpened = false;
    const waved = new Array(SLOT_COUNT).fill(false);

    const land = () => {
      if (landed) return;
      landed = true;
      ripple(to);
      if (onLanded) onLanded();
    };

    let startTs = null;
    const frame = (ts) => {
      if (startTs === null) startTs = ts;
      const el = ts - startTs;

      // ── Scan: the hairline laps the dial; each slot's mist answers.
      if (el < T_GATHER) {
        const t = el / SCAN_MS;
        const a = startA + 360 * t;
        const [x1, y1] = polar(CX, CY, HOLE + 1, a);
        const [x2, y2] = polar(CX, CY, R, a);
        scanLine.setAttribute('x1', String(x1));
        scanLine.setAttribute('y1', String(y1));
        scanLine.setAttribute('x2', String(x2));
        scanLine.setAttribute('y2', String(y2));
        if (t > 0.9) scanLine.setAttribute('stroke-opacity', (0.55 * (1 - (t - 0.9) / 0.1)).toFixed(3));
        for (let k = Math.floor(lastScanDeg / 30) + 1; k <= Math.floor((360 * t) / 30); k++) {
          const s = (activeIndex + k) % SLOT_COUNT;
          if (s !== activeIndex) mistBump(s);
        }
        lastScanDeg = 360 * t;
      } else if (scanLine.parentNode) {
        scanLine.remove();
      }

      // ── Gather: the active slot's mist thickens once.
      if (el >= T_GATHER && !gathered) {
        gathered = true;
        const m = mists[activeIndex];
        if (m && m.style.opacity !== '0') {
          m.style.transition = 'fill 220ms ease';
          m.style.fill = MIST_GATHER;
        }
      }

      // ── Write: the front rises; the mist parts ahead of it.
      if (el >= T_WRITE && el < T_SETTLE) {
        if (!parted) {
          parted = true;
          const m = mists[activeIndex];
          if (m) {
            m.style.transition = 'fill 500ms ease';
            m.style.fill = MIST_FILL;
          }
        }
        const t = (el - T_WRITE) / MAP_ANIMATION_MS;
        const f = easeInOutBack(t);
        if (f <= 1) {
          setActiveRadius(rFrom + (rTo - rFrom) * f);
          if (f >= 1) land();
        } else {
          // Constant-size pop past the honest target (deep writes clamp in
          // setActiveRadius); a zero-target write never pops.
          land();
          setActiveRadius(to > 0 ? rTo + ((f - 1) / 0.12) * POP : rTo);
        }
      }

      // ── Sharpen + settle wave.
      if (el >= T_SETTLE) {
        if (!sharpened) {
          sharpened = true;
          setActive(to); // exact honest landing frame
          land();        // belt-and-braces if the curve never crossed 1
          if (activeCore) {
            activeCore.setAttribute('stroke', topicFill(activeId));
            activeCore.setAttribute('stroke-width', '1');
            activeCore.style.transition = 'stroke-opacity 160ms ease';
            activeCore.style.strokeOpacity = '0.9';
            later(() => {
              activeCore.style.transition = 'stroke-opacity 380ms ease';
              activeCore.style.strokeOpacity = '0';
            }, 220);
          }
        }
        for (let k = 1; k < SLOT_COUNT; k++) {
          const s = (activeIndex + k) % SLOT_COUNT;
          if (!waved[s] && el >= T_SETTLE + (k - 1) * WAVE_STEP_MS) {
            waved[s] = true;
            mistBump(s);
          }
        }
      }

      if (el < T_END) {
        masterRaf = requestAnimationFrame(frame);
        return;
      }
      masterRaf = 0;
      setActive(to);
      resolve();
    };
    masterRaf = requestAnimationFrame(frame);
  });

  return {
    setActive,
    ripple,
    playUpdate,

    /**
     * Release the map's frame work: cancels the master timeline, a live
     * ripple, and every cosmetic timer, and drops the effects layer's nodes.
     * The rendered map itself stays — the card is persistent.
     */
    destroy() {
      if (masterRaf) cancelAnimationFrame(masterRaf);
      masterRaf = 0;
      if (rippleRaf) cancelAnimationFrame(rippleRaf);
      rippleRaf = 0;
      timers.forEach((id) => clearTimeout(id));
      timers.clear();
      while (fxLayer.firstChild) fxLayer.removeChild(fxLayer.firstChild);
    }
  };
}

export default buildMiniMap;
